import { TimelineStep } from '@shared/models/step-timeline.model';
import type { BulkRunHistoryItem } from '../services/campaigns.service';

const fmt = (n: number) => n.toLocaleString('de-DE');
const RUNNING_STATUSES = ['processing', 'queued'];

/**
 * Every campaign analysis is in exactly one stage; the server decides which.
 * Mirrors Kenergy-Hub-Server campaigns/workflow-stage.ts.
 */
export const WORKFLOW_STAGES = ['new', 'running', 'error', 'review', 'seen', 'flagged', 'replan', 'done', 'not_plannable', 'excluded'] as const;
export type WorkflowStage = (typeof WORKFLOW_STAGES)[number];
export type StageCounts = Record<WorkflowStage, number>;
export type StageTone = 'neutral' | 'primary' | 'warning' | 'success' | 'danger';
export type RunAction = 'detect' | 'plan' | 'full';

export interface StageMeta {
  label: string;
  tone: StageTone;
  /** Solid colour of this stage in bars and dots. */
  color: string;
  /** Tooltip: what it means. */
  tip: string;
  /** The quiet button next to the stage, when something can be done about it. */
  action?: string;
}

export const STAGE_META: Record<WorkflowStage, StageMeta> = {
  new: { label: 'Offen', tone: 'neutral', color: 'var(--s-offen)', tip: 'Noch nicht berechnet.', action: 'Berechnen' },
  running: { label: 'Läuft', tone: 'primary', color: 'var(--s-laeuft)', tip: 'Wird gerade berechnet.' },
  error: { label: 'Fehler', tone: 'danger', color: 'var(--s-fehler)', tip: 'Berechnung fehlgeschlagen. Den Grund sehen Sie in der Tabelle.', action: 'Wiederholen' },
  review: { label: 'Ungeprüft', tone: 'warning', color: 'var(--s-ungeprueft)', tip: 'Ergebnis liegt vor, noch niemand hat es angesehen.', action: 'Prüfen' },
  seen: { label: 'Angesehen', tone: 'warning', color: 'var(--s-angesehen)', tip: 'Schon angesehen, aber noch nicht entschieden.', action: 'Prüfen' },
  flagged: { label: 'Vorgemerkt', tone: 'warning', color: 'var(--s-vorgemerkt)', tip: 'Stimmt nicht und soll später korrigiert werden.', action: 'Korrigieren' },
  replan: { label: 'Zu planen', tone: 'primary', color: 'var(--s-zuplanen)', tip: 'Korrigiert, oder die Planung fehlt. Muss neu gerechnet werden.', action: 'Neu planen' },
  done: { label: 'Fertig', tone: 'success', color: 'var(--s-fertig)', tip: 'Bestätigt oder nach einer Korrektur neu geplant.', action: 'Ansehen' },
  not_plannable: { label: 'Nicht planbar', tone: 'neutral', color: 'var(--s-nichtplanbar)', tip: 'PV ist schon vorhanden oder das Dach ist zu klein.' },
  excluded: { label: 'Zurückgestellt', tone: 'neutral', color: 'var(--s-zurueck)', tip: 'Bewusst ausgelassen, der Grund steht dabei.' },
};

export const PHASES: { name: string; hint: string; stages: WorkflowStage[] }[] = [
  { name: 'Berechnen', hint: 'Die Maschine rechnet Erkennung und Planung.', stages: ['new', 'running', 'error'] },
  { name: 'Prüfen', hint: 'Sie sehen sich die Ergebnisse an und entscheiden.', stages: ['review', 'seen', 'flagged'] },
  { name: 'Neu planen', hint: 'Korrigiertes wird nachgerechnet.', stages: ['replan'] },
  { name: 'Abgeschlossen', hint: 'Hier ist nichts mehr zu tun.', stages: ['done', 'not_plannable', 'excluded'] },
];

export const REVIEW_STAGES: WorkflowStage[] = ['review', 'seen', 'flagged'];

/** Short German text for the server's reason codes, shown under the stage. */
export const REASON_LABELS: Record<string, string> = {
  detection: 'Erkennung',
  planning: 'Planung',
  unavailable: 'Nicht verfügbar',
  missing: 'Planung fehlt',
  outdated: 'Planung veraltet',
  no_roofs: 'Keine Dachfläche',
  existing_solar: 'PV vorhanden',
  roof_too_small: 'Dach zu klein',
};

export type PlanningOutcome = 'ok' | 'failed' | 'outdated' | 'none';
export const PLANNING_OUTCOMES: Record<PlanningOutcome, { label: string; classes: string }> = {
  ok: { label: 'Erfolgreich', classes: 'bg-success-soft text-success' },
  failed: { label: 'Fehlgeschlagen', classes: 'bg-danger-soft text-danger' },
  outdated: { label: 'Veraltet', classes: 'bg-warning-soft text-warning' },
  none: { label: 'Fehlt', classes: 'bg-muted text-muted-foreground' },
};

export interface WorkItem {
  id: string;
  analysisId: string;
  title: string | null;
  analysisType: string | null;
  updatedAt: string | null;
  stage: WorkflowStage;
  reason: string | null;
  message: string | null;
  canApprove: boolean;
  corrected: boolean;
  corrections: number;
  /** Corrected and planned again; a second look is optional. */
  replanned: boolean;
  checked: boolean;
  reviewKey: string;
  review: { decision: 'approved' | 'excluded' | 'seen' | 'flagged'; at: string; reason?: string } | null;
  task: number;
  status: string;
  planning: {
    state: 'current' | 'outdated' | 'unknown' | 'missing';
    outcome: PlanningOutcome;
    message: string | null;
    id: string | null;
    kwp: number | null;
    modules: number | null;
    invest: number | null;
    payback: number | null;
    roi: number | null;
  };
}

export interface WorkItemsQuery {
  stage?: WorkflowStage | null;
  q?: string;
  runId?: string | null;
  corrected?: boolean;
  recheck?: boolean;
  page?: number;
  limit?: number;
}

export interface WorkItemsResponse {
  counts: StageCounts;
  total: number;
  run: { id: string; action: RunAction | null; createdAt: string } | null;
  data: WorkItem[];
  meta: { total: number; page: number; limit: number; pages: number };
}

export const ACTION_LABELS: Record<RunAction, string> = { detect: 'Bis Analyse', plan: 'Neu planen', full: 'Komplett' };

export interface ActiveRunProgress {
  runId: string;
  action?: RunAction | null;
  status: string;
  total: number;
  dispatched: number;
  failed: number;
  skipped: number;
  startedAt: string;
  completed: number;
  pipelineFailed: number;
  processing: number;
  queued: number;
}

export interface WorkflowSummary {
  plannedInReview: number;
  replanCorrected: number;
  replanWithoutPlanning: number;
  recheck: number;
  confirmed: number;
  corrected: number;
  kwp: number;
  planned: number;
  invest: number | null;
  investAverage: number | null;
  paybackAverage: number | null;
  /** Finished plants per payback: under 8, 8-10, 10-12, 12-15, 15-20, over 20 years. */
  paybacks: number[];
}

export interface WorkflowCountsResponse {
  counts: StageCounts;
  summary: WorkflowSummary;
  total: number;
  activeRun: ActiveRunProgress | null;
}

export interface WorkflowRunRequest {
  action: RunAction;
  target: { stages?: WorkflowStage[]; ids?: string[]; runId?: string };
  templateId?: string;
  analysisType?: '2d' | '3d';
  toTask?: number;
  limit?: number;
}

export const SKIP_LABELS: Record<string, string> = {
  running: 'laufen bereits',
  unavailable: 'nicht verfügbar',
  excluded: 'zurückgestellt',
  done: 'schon fertig',
  not_plannable: 'nicht planbar',
  not_detected: 'noch nicht erkannt',
  no_roofs: 'ohne Dachfläche',
  detected: 'schon erkannt',
  not_approvable: 'nicht freigebbar',
  unchanged: 'schon so gesetzt',
  missing: 'nicht gefunden',
};

/** Preset reasons for setting an analysis aside; free text is always possible. */
export const EXCLUDE_REASONS = [
  'Dach nicht erkennbar (Bäume, Schatten)',
  'Kein Wohngebäude',
  'Denkmalschutz',
  'Bildmaterial veraltet',
];

/** "Alfred-Delp-Straße 4, 63165 Mühlheim" → street line and place line. */
export function splitAddress(title: string | null): { street: string; place: string } {
  const text = title ?? 'Ohne Adresse';
  const comma = text.indexOf(',');
  return comma < 0 ? { street: text, place: '' } : { street: text.slice(0, comma), place: text.slice(comma + 1).trim() };
}

export interface NextStep {
  label: string;
  stage: WorkflowStage;
}

const oneDecimal = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Small campaigns read better in kWp and euros than in 0,0 MWp and 0,0 Mio. €. */
export const powerFigure = (kwp: number): { value: string; unit: string } =>
  kwp < 1000 ? { value: oneDecimal(kwp), unit: 'kWp' } : { value: oneDecimal(kwp / 1000), unit: 'MWp' };
export const moneyFigure = (euros: number): { value: string; unit: string } =>
  euros < 1e6 ? { value: fmt(Math.round(euros)), unit: '€' } : { value: oneDecimal(euros / 1e6), unit: 'Mio. €' };

/** The one thing to do next, in the order the work flows: check, correct, replan, fix, run. */
export function nextStep(c: StageCounts | null, running: boolean): NextStep | null {
  if (!c || running) return null;
  if (c.review + c.seen) return { stage: c.review ? 'review' : 'seen', label: `${fmt(c.review + c.seen)} Ergebnisse prüfen` };
  if (c.flagged) return { stage: 'flagged', label: `${fmt(c.flagged)} Vorgemerkte korrigieren` };
  if (c.replan) return { stage: 'replan', label: `${fmt(c.replan)} neu planen` };
  if (c.error) return { stage: 'error', label: `${fmt(c.error)} Fehler erneut berechnen` };
  if (c.new) return { stage: 'new', label: `${fmt(c.new)} Analysen berechnen` };
  return null;
}

export interface CampaignState {
  label: string;
  stage: WorkflowStage;
}

/** Where the campaign stands, derived from its stages and runs. */
export function campaignState(c: StageCounts | null, running: boolean): CampaignState | null {
  if (!c) return null;
  if (running) return { label: 'Wird berechnet', stage: 'running' };
  if (c.review + c.seen + c.flagged) return { label: 'In Prüfung', stage: 'review' };
  if (c.replan) return { label: 'Neu planen', stage: 'replan' };
  if (c.new + c.error) return { label: 'Bereit', stage: 'new' };
  return { label: 'Abgeschlossen', stage: 'done' };
}

/** The three steps people talk about, and the five tasks behind them. */
export const PIPELINE_STEPS = [
  { label: 'Geodaten', first: 1, last: 1 },
  { label: 'Analyse', first: 2, last: 3 },
  { label: 'Planung', first: 4, last: 5 },
];
export const PIPELINE_TASKS = [
  { label: 'Geocut', first: 1, last: 1 },
  { label: 'Erkennung', first: 2, last: 2 },
  { label: 'Ausrichtung', first: 3, last: 3 },
  { label: 'Belegung', first: 4, last: 4 },
  { label: 'Planung', first: 5, last: 5 },
];

/**
 * The Hub's step rail for a set of analyses. Hovering or choosing a step lights
 * up what would run, from the earliest step any of them still needs.
 */
export function runTimeline(
  rows: WorkItem[],
  technical: boolean,
  options: { hoveredLast?: number | null; lockedLast?: number | null; rangeLast?: number | null; from?: number; clickable: boolean; actionTitle?: string; compact?: boolean },
): TimelineStep[] {
  const list = technical ? PIPELINE_TASKS : PIPELINE_STEPS;
  const target = options.hoveredLast ?? options.lockedLast ?? options.rangeLast ?? null;
  const pending = rows
    .filter((row) => !RUNNING_STATUSES.includes(row.status))
    .map((row) => (row.status === 'completed' ? row.task + 1 : Math.max(1, row.task)))
    .filter((task) => target !== null && task <= target);
  const start = options.from ?? (pending.length ? Math.min(...pending) : 1);
  const count = (test: (row: WorkItem) => boolean) => rows.reduce((all, row) => all + (test(row) ? 1 : 0), 0);
  return [
    { id: 'created', label: 'Erstellt', state: 'completed', description: `${fmt(rows.length)} Analysen`, clickable: false },
    ...list.map((step): TimelineStep => {
      const done = count((row) => row.task > step.last || (row.task === step.last && row.status === 'completed'));
      const active = count((row) => row.task >= step.first && row.task <= step.last && RUNNING_STATUSES.includes(row.status));
      const failed = count((row) => row.task >= step.first && row.task <= step.last && row.status === 'failed');
      const locked = options.lockedLast === step.last;
      // A few failures do not paint the whole campaign red; they stand in the meta line.
      const state: TimelineStep['state'] = active ? 'current' : rows.length && done === rows.length ? 'completed' : failed && done + failed === rows.length ? 'error' : 'upcoming';
      const clickable = options.clickable && rows.length > 0 && step.last >= (options.from ?? 1);
      // As on the analysis page: the step under the pointer offers what clicking it does.
      const offered = locked || (clickable && options.hoveredLast === step.last);
      return {
        id: step.last,
        label: step.label,
        state,
        description: `${fmt(done)} / ${fmt(rows.length)}${options.compact ? '' : ' fertig'}`,
        meta: active ? `${fmt(active)} läuft${failed ? ` · ${fmt(failed)} Fehler` : ''}` : failed ? `${fmt(failed)} Fehler` : undefined,
        clickable,
        nodeActionIcon: offered ? (!locked && (state === 'completed' || state === 'error') ? 'lucideRotateCcw' : 'lucidePlay') : undefined,
        nodeActionTitle: offered ? options.actionTitle ?? `Bis ${step.label} berechnen` : undefined,
        nodeActionDisabled: !offered,
        data: { firstTask: step.first, lastTask: step.last, executionRange: target !== null && step.last >= start && step.first <= target, executionPending: locked },
      };
    }),
  ];
}

/** One execution in words and numbers, in its table row and its panel. */
const RUN_STATUS: Record<string, { label: string; classes: string }> = {
  pending: { label: 'Wartet', classes: 'bg-primary/10 text-primary' },
  running: { label: 'Läuft', classes: 'bg-primary/10 text-primary' },
  completed: { label: 'Abgeschlossen', classes: 'bg-success-soft text-success' },
  errors: { label: 'Mit Fehlern', classes: 'bg-danger-soft text-danger' },
  failed: { label: 'Fehlgeschlagen', classes: 'bg-danger-soft text-danger' },
  cancelled: { label: 'Abgebrochen', classes: 'bg-muted text-muted-foreground' },
};
export function runStatus(run: BulkRunHistoryItem): { label: string; classes: string } {
  return run.status === 'completed' && run.failed ? RUN_STATUS['errors'] : RUN_STATUS[run.status] ?? RUN_STATUS['pending'];
}
export function runKind(run: BulkRunHistoryItem): string {
  return run.action ? ACTION_LABELS[run.action] : `Technisch · Schritte ${run.startTask}–${run.endTask}`;
}
export function runDuration(run: BulkRunHistoryItem): string {
  if (!run.startedAt) return '–';
  const seconds = Math.max(0, Math.round(((run.completedAt ? new Date(run.completedAt).getTime() : Date.now()) - new Date(run.startedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
}
export function runSummary(run: BulkRunHistoryItem): string {
  if (run.status === 'failed') {
    const message = run.errorMessage?.trim();
    return message && message.toLowerCase() !== 'error' ? message : 'SolarInsight konnte die Berechnung nicht starten.';
  }
  // Older runs only recorded what they dispatched.
  const parts = [run.succeeded || run.action ? `${fmt(run.succeeded)} Ergebnisse` : `${fmt(run.dispatched)} gestartet`];
  if (run.failed) parts.push(`${fmt(run.failed)} Fehler`);
  if (run.skipped) parts.push(`${fmt(run.skipped)} ausgelassen`);
  return parts.join(' · ');
}
export function runSegments(run: BulkRunHistoryItem) {
  return [
    { grow: run.succeeded, color: STAGE_META.done.color },
    { grow: run.failed, color: STAGE_META.error.color },
    { grow: run.skipped, color: STAGE_META.new.color },
  ].filter((segment) => segment.grow);
}

export type StepState = 'done' | 'run' | 'err' | 'warn' | '';
const STEP_WORD: Record<StepState, string> = { done: 'fertig', warn: 'veraltet', err: 'Fehler', run: 'läuft', '': 'offen' };

/** How far an analysis got technically: Geodaten, Analyse, Planung. */
export function stepsOf(item: WorkItem): StepState[] {
  const running = item.status === 'queued' || item.status === 'processing';
  return PIPELINE_STEPS.map((step, index) => {
    const reached = item.task > step.last || (item.task === step.last && item.status === 'completed');
    const here = item.task >= step.first && item.task <= step.last;
    if (index === 2) {
      if (item.planning.outcome === 'ok') return 'done';
      if (running && here) return 'run';
      if (item.planning.outcome === 'failed') return 'err';
      if (item.planning.outcome === 'outdated') return 'warn';
      return '';
    }
    if (reached) return 'done';
    if (running && here) return 'run';
    if (item.status === 'failed' && here) return 'err';
    return '';
  });
}

export function stepsTitle(item: WorkItem): string {
  const steps = stepsOf(item);
  return `Geodaten ${STEP_WORD[steps[0]]} · Analyse ${STEP_WORD[steps[1]]} · Planung ${STEP_WORD[steps[2]]}`;
}

/** Markers on a row, for the hint column and the filter. */
export function marksOf(item: WorkItem): string[] {
  const marks: string[] = [];
  if (item.corrections) marks.push(item.corrections > 1 ? `Korrigiert ×${item.corrections}` : 'Korrigiert');
  if (item.replanned) marks.push('Nachgeplant');
  if (item.message) marks.push(item.message);
  else if (item.reason && REASON_LABELS[item.reason] && item.stage !== 'error') marks.push(REASON_LABELS[item.reason]);
  if (item.planning.outcome === 'failed' && item.stage !== 'error') marks.push('Planung fehlgeschlagen');
  return marks;
}
